import React, { useEffect, useState, useCallback } from 'react'
import { useParams, Link, useHistory } from 'react-router-dom'
import { Card, Row, Col, Badge, Button, Table, Alert, Nav, Tab, Modal } from '@themesberg/react-bootstrap'
import { useCookies } from 'react-cookie'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { getCartGroupDetail, refundCartGroup, refundTransactions, distributeReward, updateCartGroup, removeCartGroup } from '../../../controller/api/cartGroup'
import { Routes } from '../../../routes'

const CartGroupDetail = () => {
  const { id } = useParams()
  const history = useHistory()
  const [cookies] = useCookies(['lottmekong'])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('info')
  const [refunding, setRefunding] = useState(false)
  const [refundingSelected, setRefundingSelected] = useState(false)
  const [distributing, setDistributing] = useState(false)
  const [activating, setActivating] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [selectedTransactionIds, setSelectedTransactionIds] = useState([])

  useEffect(() => {
    document.title = `Chi tiết nhóm mua chung - ${id || ''}`
  }, [id])

  const fetchDetail = useCallback(async () => {
    if (!id) {
      setError('Không tìm thấy ID nhóm')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError('')

      const response = await getCartGroupDetail(cookies.lottmekong, id)

      if (response && response.error === 0 && response.data) {
        setData(response.data)
      } else {
        setError(response?.message || 'Không tìm thấy nhóm mua chung với ID này')
      }
    } catch (err) {
      console.error('Error fetching cart group detail:', err)
      setError('Có lỗi xảy ra khi tải dữ liệu')
    } finally {
      setLoading(false)
    }
  }, [id, cookies.lottmekong])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  const handleRefund = useCallback(async () => {
    if (!id || !window.confirm('Hủy nhóm và hoàn tiền cho tất cả thành viên? Hành động không thể hoàn tác.')) {
      return
    }

    try {
      setRefunding(true)
      const response = await refundCartGroup(cookies.lottmekong, id)

      if (response && response.error === 0) {
        alert('Hủy nhóm và hoàn tiền thành công')
        fetchDetail()
      } else {
        alert(response?.message || 'Có lỗi xảy ra khi hoàn tiền')
      }
    } catch (err) {
      console.error('Error refunding cart group:', err)
      alert('Có lỗi xảy ra khi hoàn tiền')
    } finally {
      setRefunding(false)
    }
  }, [id, cookies.lottmekong, fetchDetail])

  const handleRefundSelected = useCallback(async () => {
    if (!id || !selectedTransactionIds.length) return
    if (!window.confirm(`Hoàn tiền ${selectedTransactionIds.length} giao dịch đã chọn?`)) return

    try {
      setRefundingSelected(true)
      const response = await refundTransactions(cookies.lottmekong, id, selectedTransactionIds)
      if (response && response.error === 0) {
        alert('Hoàn tiền thành công')
        setSelectedTransactionIds([])
        fetchDetail()
      } else {
        alert(response?.message || 'Có lỗi xảy ra khi hoàn tiền')
      }
    } catch (err) {
      console.error('Error refunding transactions:', err)
      alert('Có lỗi xảy ra khi hoàn tiền')
    } finally {
      setRefundingSelected(false)
    }
  }, [id, selectedTransactionIds, cookies.lottmekong, fetchDetail])

  const contributeTransactions = data?.transactions?.filter((tx) => tx.transactionType === 'BUYTICKET') || []
  const toggleTransactionSelect = (txId) => {
    setSelectedTransactionIds((prev) =>
      prev.includes(txId) ? prev.filter((id) => id !== txId) : [...prev, txId]
    )
  }
  const toggleSelectAllContributions = () => {
    if (selectedTransactionIds.length >= contributeTransactions.length) {
      setSelectedTransactionIds([])
    } else {
      setSelectedTransactionIds(contributeTransactions.map((tx) => tx.id))
    }
  }

  const handleDistributeReward = useCallback(async () => {
    if (!id || !window.confirm('Bạn có chắc chắn muốn phân phối giải thưởng cho các thành viên?')) {
      return
    }

    try {
      setDistributing(true)
      const response = await distributeReward(cookies.lottmekong, id)

      if (response && response.error === 0) {
        alert('Phân phối giải thưởng thành công')
        fetchDetail() // Refresh data
      } else {
        alert(response?.message || 'Có lỗi xảy ra khi phân phối giải thưởng')
      }
    } catch (err) {
      console.error('Error distributing reward:', err)
      alert('Có lỗi xảy ra khi phân phối giải thưởng')
    } finally {
      setDistributing(false)
    }
  }, [id, cookies.lottmekong, fetchDetail])

  const handleActivate = useCallback(async () => {
    if (!id || !window.confirm('Bạn có chắc chắn muốn kích hoạt nhóm này?')) {
      return
    }

    try {
      setActivating(true)
      const response = await updateCartGroup(cookies.lottmekong, id, 1)

      if (response && response.error === 0) {
        alert('Kích hoạt nhóm thành công')
        fetchDetail() // Refresh data
      } else {
        alert(response?.message || 'Có lỗi xảy ra khi kích hoạt nhóm')
      }
    } catch (err) {
      console.error('Error activating cart group:', err)
      alert('Có lỗi xảy ra khi kích hoạt nhóm')
    } finally {
      setActivating(false)
    }
  }, [id, cookies.lottmekong, fetchDetail])

  const handleRemove = useCallback(async () => {
    if (!id || !window.confirm('Bạn có chắc chắn muốn xóa nhóm này? Hành động không thể hoàn tác.')) {
      return
    }

    try {
      setRemoving(true)
      const response = await removeCartGroup(cookies.lottmekong, id)

      if (response && response.error === 0) {
        alert('Xóa nhóm thành công')
        // Navigate to inactive list page
        history.push(Routes.VietlottGroupPending.path)
      } else {
        alert(response?.message || 'Có lỗi xảy ra khi xóa nhóm')
      }
    } catch (err) {
      console.error('Error removing cart group:', err)
      alert('Có lỗi xảy ra khi xóa nhóm')
    } finally {
      setRemoving(false)
    }
  }, [id, cookies.lottmekong])

  const getStatusInfo = () => {
    if (!data) return { text: 'N/A', color: 'secondary' }

    const { status, actualValue, estimatedValue } = data

    if (status === 0) {
      return { text: 'Không hoạt động', color: 'warning' }
    } else if (status === 1) {
      const percent = actualValue && estimatedValue ? Number(((actualValue / estimatedValue) * 100).toFixed(3)) : 0
      return { text: `Đang góp (${percent}%)`, color: 'primary' }
    } else if (status === 2) {
      return { text: 'Đã hoàn thành', color: 'success' }
    } else if (status === 3) {
      return { text: 'Đã bị hủy', color: 'danger' }
    } else if (status === 4) {
      return { text: 'Trúng thưởng', color: 'info' }
    } else if (status === 5) {
      return { text: 'Không trúng thưởng', color: 'secondary' }
    } else if (status === 6) {
      return { text: 'Đã bị xóa', color: 'dark' }
    }
    return { text: 'Không xác định', color: 'secondary' }
  }

  const formatDate = (date) => {
    if (!date) return 'N/A'
    return moment(date).format('DD/MM/YYYY HH:mm')
  }

  const formatDeadline = () => {
    const { expiredAt } = data || {}
    return expiredAt ? formatDate(expiredAt) : 'N/A'
  }

  const formatCurrency = (value) => {
    if (!value) return '0'
    return new Intl.NumberFormat('vi-VN').format(value)
  }

  const getPlayMethodLabel = (method) => {
    const methodMap = {
      'GROUP_NORMAL_4': 'Bao thường 4 số',
      'GROUP_NORMAL_5': 'Bao thường 5 số',
      'GROUP_NORMAL_6': 'Bao thường 6 số',
      'GROUP_NORMAL_7': 'Bao thường 7 số',
      'GROUP_NORMAL_8': 'Bao thường 8 số',
      'GROUP_NORMAL_9': 'Bao thường 9 số',
      'GROUP_NORMAL_10': 'Bao thường 10 số',
      'GROUP_NORMAL_11': 'Bao thường 11 số',
      'GROUP_NORMAL_12': 'Bao thường 12 số',
      'GROUP_NORMAL_13': 'Bao thường 13 số',
      'GROUP_NORMAL_14': 'Bao thường 14 số',
      'GROUP_NORMAL_15': 'Bao thường 15 số',
      'GROUP_5': 'Bao 5 số',
      'GROUP_6': 'Bao 6 số',
      'GROUP_7': 'Bao 7 số',
      'GROUP_8': 'Bao 8 số',
      'GROUP_9': 'Bao 9 số',
      'GROUP_10': 'Bao 10 số',
      'GROUP_11': 'Bao 11 số',
      'GROUP_12': 'Bao 12 số',
      'GROUP_13': 'Bao 13 số',
      'GROUP_14': 'Bao 14 số',
      'GROUP_15': 'Bao 15 số',
      'GROUP_18': 'Bao 18 số',
    }
    return methodMap[method] || method
  }

  const getGameName = (gameId) => {
    const gameMap = {
      'lotto535': 'LOTO 5/35',
      'keno': 'Keno',
      'mega': 'Mega 6/45',
      'power': 'Power 6/55',
      'max3d': 'Max3D/3D+',
      'max3dpro': 'Max3D Pro'
    }
    return gameMap[gameId] || gameId
  }

  const statusInfo = getStatusInfo()
  const showWarning = data && data.actualValue > data.estimatedValue

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Đang tải...</span>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <Card border="light" className="shadow-sm">
        <Card.Body className="text-center py-5">
          <h5 className="text-danger">{error || 'Không tìm thấy dữ liệu'}</h5>
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      {showWarning && (
        <Alert variant="warning" className="mb-3" id="warning-actual-value">
          <Alert.Heading>Cảnh báo: Giá trị thực tế &gt; giá trị ước tính</Alert.Heading>
          <p className="mb-2">
            Giá trị thực tế (<strong>{formatCurrency(data.actualValue)} VNĐ</strong>) lớn hơn giá trị ước tính (
            <strong>{formatCurrency(data.estimatedValue)} VNĐ</strong>).
            <br />
            Chênh lệch: <strong>{formatCurrency(data.actualValue - data.estimatedValue)} VNĐ</strong>
          </p>
          <p className="text-muted small mb-2">
            Bạn có thể hủy nhóm và hoàn tiền cho tất cả thành viên, hoặc chọn từng giao dịch để hoàn tiền trong tab Giao dịch.
          </p>
          {data.status === 1 && (
            <Button variant="danger" onClick={handleRefund} disabled={refunding}>
              {refunding ? 'Đang xử lý...' : 'Hủy nhóm & hoàn tiền cho tất cả'}
            </Button>
          )}
        </Alert>
      )}

      <Card border="light" className="shadow-sm mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Chi tiết nhóm mua chung</h5>
          <div className="d-flex align-items-center gap-2">
            {showWarning && (
              <Badge bg="warning" text="dark">Giá trị thực tế &gt; ước tính</Badge>
            )}
            {data.tickets && data.tickets.length > 0 && (
              <Button variant="info" size="sm" as={Link} to={Routes.VietlottTicketDetail.path.replace(':id', id)} target="_blank">
                Xem vé
                <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-1" />
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="info">Thông tin</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="members">Thành viên ({data.members?.length || 0})</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="transactions">Giao dịch ({data.transactions?.length || 0})</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="reward">Giải thưởng</Nav.Link>
              </Nav.Item>
              {(data.contributionNumbers?.length > 0 || data.specialContributionNumbers?.length > 0) && (
                <Nav.Item>
                  <Nav.Link eventKey="numbers">Tỷ lệ % mỗi số</Nav.Link>
                </Nav.Item>
              )}
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="info">
                <Row className="mb-3">
                  <Col md={4}>
                    <strong>Mã nhóm:</strong>
                  </Col>
                  <Col md={8}>
                    <code>{data.id}</code>
                  </Col>
                </Row>

                {data.name && (
                  <Row className="mb-3">
                    <Col md={4}>
                      <strong>Tên nhóm:</strong>
                    </Col>
                    <Col md={8}>
                      {data.name}
                    </Col>
                  </Row>
                )}

                <Row className="mb-3">
                  <Col md={4}>
                    <strong>Trò chơi:</strong>
                  </Col>
                  <Col md={8}>
                    {getGameName(data.gameId)}
                    {/* {data.gameId && (
                      <img
                        alt=""
                        src={require(`../../../assets/img/logo-${data.gameId}.png`)}
                        className="img-thumbnail ms-2"
                        style={{ height: 50 }}
                      />
                    )} */}
                  </Col>
                </Row>

                {/* <Row className="mb-3">
                  <Col md={4}>
                    <strong>Kỳ quay số:</strong>
                  </Col>
                  <Col md={8}>
                    {data.gamePeriod || 'N/A'}
                  </Col>
                </Row> */}

                <Row className="mb-3">
                  <Col md={4}>
                    <strong>Cách chơi:</strong>
                  </Col>
                  <Col md={8}>
                    {getPlayMethodLabel(data.playMethod) || data.playMethod || 'N/A'}
                  </Col>
                </Row>

                {data.numbers && (
                  <Row className="mb-3">
                    <Col md={4}>
                      <strong>Dãy số:</strong>
                    </Col>
                    <Col md={8}>
                      {data.gameId === 'lotto535' ? (
                        <>
                          {(() => {
                            const nums = data.numbers.trim().split(/\s+/);
                            const regularNums = nums.slice(0, -1);
                            const specialNum = nums[nums.length - 1];
                            return (
                              <span className="d-flex align-items-center gap-2 flex-wrap">
                                {regularNums.map((num, idx) => (
                                  <span
                                    key={idx}
                                    className="badge bg-primary rounded-circle d-inline-flex align-items-center justify-content-center"
                                    style={{ width: '32px', height: '32px', fontSize: '14px' }}
                                  >
                                    {num}
                                  </span>
                                ))}
                                <span
                                  className="badge rounded-circle d-inline-flex align-items-center justify-content-center"
                                  style={{ width: '32px', height: '32px', fontSize: '14px', backgroundColor: '#ffc107', color: '#000' }}
                                  title="Số đặc biệt"
                                >
                                  {specialNum}
                                </span>
                              </span>
                            );
                          })()}
                        </>
                      ) : (
                        <code>{data.numbers}</code>
                      )}
                    </Col>
                  </Row>
                )}

                <Row className="mb-3">
                  <Col md={4}>
                    <strong>Giá trị ước tính:</strong>
                  </Col>
                  <Col md={8}>
                    {formatCurrency(data.estimatedValue)} VNĐ
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={4}>
                    <strong>Giá trị thực tế:</strong>
                  </Col>
                  <Col md={8}>
                    {formatCurrency(data.actualValue)} VNĐ
                    {showWarning && (
                      <span className="ms-2 text-warning fw-semibold">(Vượt ước tính)</span>
                    )}
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={4}>
                    <strong>Hạn chót:</strong>
                  </Col>
                  <Col md={8}>
                    {formatDeadline()}
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={4}>
                    <strong>Trạng thái:</strong>
                  </Col>
                  <Col md={8}>
                    <Badge bg={statusInfo.color}>{statusInfo.text}</Badge>
                    {data.status === 0 && (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          className="ms-3"
                          onClick={handleActivate}
                          disabled={activating || removing}
                        >
                          {activating ? 'Đang xử lý...' : 'Kích hoạt'}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          className="ms-2"
                          onClick={handleRemove}
                          disabled={removing || activating}
                        >
                          {removing ? 'Đang xóa...' : 'Xóa nhóm'}
                        </Button>
                      </>
                    )}
                    {data.status === 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-3"
                        onClick={handleRefund}
                        disabled={refunding}
                      >
                        {refunding ? 'Đang xử lý...' : 'Dừng góp & hoàn tiền (nếu có)'}
                      </Button>
                    )}
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={4}>
                    <strong>Người tạo:</strong>
                  </Col>
                  <Col md={8}>
                    {data.isCreatedByAdmin ? 'Admin' : (data.createdByUserName || data.createdByUserPhone || 'Người dùng')}
                    {data.createdByUserPhone && !data.isCreatedByAdmin && (
                      <span className="text-muted ms-2">({data.createdByUserPhone})</span>
                    )}
                  </Col>
                </Row>

                <hr />

                <h6 className="mb-3">Thời gian</h6>

                <Row className="mb-2">
                  <Col md={4}>
                    <strong>Tạo lúc:</strong>
                  </Col>
                  <Col md={8}>
                    {formatDate(data.createdAt)}
                  </Col>
                </Row>

                {data.completedAt && (
                  <Row className="mb-2">
                    <Col md={4}>
                      <strong>Hoàn thành lúc:</strong>
                    </Col>
                    <Col md={8}>
                      {formatDate(data.completedAt)}
                    </Col>
                  </Row>
                )}

                {data.generatedTicketAt && (
                  <Row className="mb-2">
                    <Col md={4}>
                      <strong>Tạo vé lúc:</strong>
                    </Col>
                    <Col md={8}>
                      {formatDate(data.generatedTicketAt)}
                    </Col>
                  </Row>
                )}

                {data.canceledAt && (
                  <Row className="mb-2">
                    <Col md={4}>
                      <strong>Hủy lúc:</strong>
                    </Col>
                    <Col md={8}>
                      {formatDate(data.canceledAt)}
                    </Col>
                  </Row>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="members">
                {data.members && data.members.length > 0 ? (
                  <Table responsive className="align-items-center table-bordered">
                    <thead className="text-center">
                      <tr>
                        <th>STT</th>
                        <th>Người dùng</th>
                        <th>Số điện thoại</th>
                        <th>Giá trị đóng góp</th>
                        <th>Tổng giá trị</th>
                        <th>Tỷ lệ %</th>
                        <th>Trạng thái</th>
                        <th>Ngày tham gia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.members.map((member, index) => (
                        <tr key={member.id}>
                          <td className="text-center">{index + 1}</td>
                          <td>{member.userName || 'N/A'}</td>
                          <td>{member.userPhone || 'N/A'}</td>
                          <td className="text-end">{formatCurrency(member.value)} VNĐ</td>
                          <td className="text-end">{formatCurrency(member.totalValue)} VNĐ</td>
                          <td className="text-center">{member.percent}%</td>
                          <td className="text-center">
                            <Badge bg={member.status === 1 ? 'success' : 'secondary'}>
                              {member.status === 1 ? 'Hoạt động' : 'Không hoạt động'}
                            </Badge>
                          </td>
                          <td className="text-center">{formatDate(member.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-center text-muted">Chưa có thành viên</p>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="transactions">
                {data.transactions && data.transactions.length > 0 ? (
                  <>
                    {contributeTransactions.length > 0 && data.status === 1 && (
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="selectAllContributions"
                            checked={selectedTransactionIds.length === contributeTransactions.length && contributeTransactions.length > 0}
                            onChange={toggleSelectAllContributions}
                          />
                          <label className="form-check-label" htmlFor="selectAllContributions">
                            Chọn tất cả giao dịch Góp ({contributeTransactions.length})
                          </label>
                        </div>
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={handleRefundSelected}
                          disabled={refundingSelected || selectedTransactionIds.length === 0}
                        >
                          {refundingSelected ? 'Đang xử lý...' : `Hoàn tiền đã chọn (${selectedTransactionIds.length})`}
                        </Button>
                      </div>
                    )}
                    <Table responsive className="align-items-center table-bordered">
                      <thead className="text-center">
                        <tr>
                          <th style={{ width: 40 }} title="Chọn để hoàn tiền" />
                          <th>STT</th>
                          <th>Loại</th>
                          <th>Mã giao dịch</th>
                          <th>Thành viên</th>
                          <th>Người dùng</th>
                          <th>Phương thức</th>
                          <th>Giá trị</th>
                          <th>Phí</th>
                          <th>Tổng</th>
                          <th>Trạng thái</th>
                          <th>Ngày tạo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.transactions.map((tx, index) => (
                          <tr key={tx.id}>
                            <td className="text-center">
                              {tx.transactionType === 'BUYTICKET' && data.status === 1 && (
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedTransactionIds.includes(tx.id)}
                                  onChange={() => toggleTransactionSelect(tx.id)}
                                />
                              )}
                            </td>
                            <td className="text-center">{index + 1}</td>
                            <td className="text-center">
                              <Badge bg={tx.transactionType === 'CASHBACK' ? 'warning' : 'primary'}>
                                {tx.transactionType === 'CASHBACK' ? 'Hoàn tiền' : 'Góp'}
                              </Badge>
                            </td>
                            <td><code>{tx.referenceNumber || tx.id}</code></td>
                            <td>
                              {tx.member ? (
                                <>
                                  {tx.member.userName || 'N/A'}
                                  <br />
                                  <small className="text-muted">({tx.member.userPhone || 'N/A'})</small>
                                </>
                              ) : 'N/A'}
                            </td>
                            <td>
                              {tx.user.name || 'N/A'}
                              <br />
                              <small className="text-muted">({tx.user.phone || 'N/A'})</small>
                            </td>
                            <td>{tx.paymentMethod || 'N/A'}</td>
                            <td className="text-end">{formatCurrency(tx.value)} VNĐ</td>
                            <td className="text-end">{formatCurrency(tx.fee)} VNĐ</td>
                            <td className="text-end"><strong>{formatCurrency(tx.total)} VNĐ</strong></td>
                            <td className="text-center">
                              <Badge bg={tx.status === 1 ? 'success' : 'secondary'}>
                                {tx.status === 1 ? 'Thành công' : 'Khác'}
                              </Badge>
                            </td>
                            <td className="text-center">{formatDate(tx.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                ) : (
                  <p className="text-center text-muted">Chưa có giao dịch</p>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="reward">
                <Row className="mb-3">
                  <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0">Tổng giải thưởng:</h6>
                        <h4 className="text-success mb-0">{formatCurrency(data.rewardAmount || 0)} VNĐ</h4>
                      </div>
                      {data.isDistributedReward !== 1 && (
                        <Button
                          variant="primary"
                          onClick={handleDistributeReward}
                          disabled={distributing || !data.rewardAmount || data.rewardAmount === 0}
                        >
                          {distributing ? 'Đang phân phối...' : 'Phân phối giải thưởng'}
                        </Button>
                      )}
                      {data.isDistributedReward === 1 && (
                        <Badge bg="success">Đã phân phối</Badge>
                      )}
                    </div>
                  </Col>
                </Row>

                {data.members && data.members.length > 0 ? (
                  <Table responsive className="align-items-center table-bordered">
                    <thead className="text-center">
                      <tr>
                        <th>STT</th>
                        <th>Người dùng</th>
                        <th>Số điện thoại</th>
                        <th>Giá trị đóng góp</th>
                        <th>Tỷ lệ %</th>
                        <th>Giải thưởng</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.members.map((member, index) => (
                        <tr key={member.id}>
                          <td className="text-center">{index + 1}</td>
                          <td>{member.userName || 'N/A'}</td>
                          <td>{member.userPhone || 'N/A'}</td>
                          <td className="text-end">{formatCurrency(member.value)} VNĐ</td>
                          <td className="text-center">
                            {typeof member.percent === 'number'
                              ? Number(member.percent.toFixed(3))
                              : (data.estimatedValue && member.value
                                ? Number(((member.value / data.estimatedValue) * 100).toFixed(3))
                                : 0)}%
                          </td>
                          <td className="text-end text-success">
                            <strong>{formatCurrency(member.rewardAmount || 0)} VNĐ</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-center text-muted">Chưa có thành viên</p>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="numbers">
                {data.contributionNumbers && data.contributionNumbers.length > 0 && (
                  <>
                    <h6 className="mb-3">Số thường</h6>
                    <Table responsive className="align-items-center table-bordered mb-4">
                      <thead className="text-center">
                        <tr>
                          <th style={{ width: 60 }}>Số</th>
                          <th>Giá trị (VNĐ)</th>
                          <th>Tỷ lệ %</th>
                          <th style={{ width: '40%' }}>Biểu đồ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.contributionNumbers.map((item, index) => (
                          <tr key={index} className={item.highlight ? 'table-warning' : ''}>
                            <td className="text-center">
                              <span
                                className={`badge rounded-circle d-inline-flex align-items-center justify-content-center ${item.highlight ? 'bg-primary' : 'bg-secondary'}`}
                                style={{ width: '32px', height: '32px', fontSize: '14px' }}
                              >
                                {item.number}
                              </span>
                            </td>
                            <td className="text-end">{formatCurrency(item.value)}</td>
                            <td className="text-center fw-semibold">{item.percent}%</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  style={{
                                    width: `${Math.max(item.percent, 1)}%`,
                                    height: '20px',
                                    backgroundColor: item.highlight ? '#0d6efd' : '#6c757d',
                                    borderRadius: '4px',
                                    transition: 'width 0.3s ease',
                                    minWidth: '4px',
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}

                {data.gameId === 'lotto535' && data.specialContributionNumbers && data.specialContributionNumbers.length > 0 && (
                  <>
                    <h6 className="mb-3">
                      <span className="badge bg-warning text-dark me-2">Đặc biệt</span>
                      Số đặc biệt (1–12)
                    </h6>
                    <Table responsive className="align-items-center table-bordered">
                      <thead className="text-center">
                        <tr style={{ backgroundColor: '#fff8e1' }}>
                          <th style={{ width: 60 }}>Số</th>
                          <th>Giá trị (VNĐ)</th>
                          <th>Tỷ lệ %</th>
                          <th style={{ width: '40%' }}>Biểu đồ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.specialContributionNumbers.map((item, index) => (
                          <tr key={index}>
                            <td className="text-center">
                              <span
                                className="badge rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{ width: '32px', height: '32px', fontSize: '14px', backgroundColor: '#ffc107', color: '#000' }}
                              >
                                {item.number}
                              </span>
                            </td>
                            <td className="text-end">{formatCurrency(item.value)}</td>
                            <td className="text-center fw-semibold">{item.percent}%</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  style={{
                                    width: `${Math.max(item.percent, 1)}%`,
                                    height: '20px',
                                    backgroundColor: '#ffc107',
                                    borderRadius: '4px',
                                    transition: 'width 0.3s ease',
                                    minWidth: '4px',
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}

                {(!data.contributionNumbers || data.contributionNumbers.length === 0) &&
                 (!data.specialContributionNumbers || data.specialContributionNumbers.length === 0) && (
                  <p className="text-center text-muted">Chưa có dữ liệu tỷ lệ số</p>
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>
    </>
  )
}

export default CartGroupDetail
